// Voice recording. Opus at 24 kbps is ~180 KB per minute: plenty for speech
// transcription and cheap to store.

const MAX_MS = 5 * 60 * 1000;

export type Recording = { blob: Blob; ms: number };

export class Recorder {
  private rec?: MediaRecorder;
  private stream?: MediaStream;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private limit?: ReturnType<typeof setTimeout>;

  /**
   * Whether the page's Permissions-Policy lets this document use the microphone at all.
   * A site that sends `microphone=()` blocks it for every script, ours included, and the
   * browser rejects getUserMedia without ever showing a prompt.
   */
  static allowedByPolicy(): boolean {
    const d = document as Document & {
      permissionsPolicy?: { allowsFeature(f: string): boolean };
      featurePolicy?: { allowsFeature(f: string): boolean };
    };
    const policy = d.permissionsPolicy ?? d.featurePolicy;
    try {
      return policy ? policy.allowsFeature("microphone") : true;
    } catch {
      return true;
    }
  }

  static supported(): boolean {
    return typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  }

  get elapsed(): number {
    return this.startedAt ? Date.now() - this.startedAt : 0;
  }

  get active(): boolean {
    return this.rec?.state === "recording";
  }

  /** Triggers the browser's microphone permission prompt the first time. */
  async start(onAutoStop: () => void): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    const mimeType = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4"].find((t) => MediaRecorder.isTypeSupported(t));
    this.rec = new MediaRecorder(this.stream, { mimeType, audioBitsPerSecond: 24000 });
    this.chunks = [];
    this.rec.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
    this.rec.start(1000);
    this.startedAt = Date.now();
    this.limit = setTimeout(onAutoStop, MAX_MS);
  }

  stop(): Promise<Recording | null> {
    clearTimeout(this.limit);
    const rec = this.rec;
    if (!rec || rec.state === "inactive") return Promise.resolve(null);
    const ms = this.elapsed;
    return new Promise((resolve) => {
      rec.onstop = () => {
        this.release();
        resolve({ blob: new Blob(this.chunks, { type: rec.mimeType || "audio/webm" }), ms });
      };
      rec.stop();
    });
  }

  cancel() {
    clearTimeout(this.limit);
    if (this.rec && this.rec.state !== "inactive") this.rec.stop();
    this.release();
  }

  private release() {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = undefined;
    this.startedAt = 0;
  }
}
